/**
 * Estado de interface. Preferências persistem por navegador (localStorage);
 * o que é efêmero (busca, destaque, data de referência) não.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { type BoardScope, type ColorMode, DEFAULT_SCOPE, localToday, type SortMode } from '@quadro/domain';

export type MapColorMode = 'tipo' | 'adm' | 'pend' | 'tarefas' | 'exposicao';
export interface MapPrefs {
  depth: number;
  showHolders: boolean;
  color: MapColorMode;
  edgeLabel: 'pct' | 'valor' | 'nenhum';
  edgeWidth: boolean;
  orientation: 'DOWN' | 'RIGHT';
  minimap: boolean;
  structure: string;
  panel: boolean;
  tab: 'mapa' | 'exposicao' | 'participacoes' | 'pendencias';
  source: 'live' | 'file';
}

interface Prefs {
  scope: BoardScope;
  colorBy: ColorMode;
  sort: SortMode;
  insights: boolean;
  showOlderDone: boolean;
  agendaMode: 'week' | 'month';
  hiddenCalendars: string[];
  notesFilter: 'all' | 'pinned';
  sidebarHidden: boolean;
  map: MapPrefs;
}
interface Ephemeral {
  search: string;
  noteSearch: string;
  highlight: string | null;
  colorFilter: string[];
  refDate: string;
  selectedDay: string;
  selectedNote: string | null;
  sidebarOpen: boolean;
}

export const DEFAULT_MAP_PREFS: MapPrefs = {
  depth: 2, showHolders: true, color: 'tipo', edgeLabel: 'pct', edgeWidth: false, orientation: 'DOWN',
  minimap: true, structure: '', panel: true, tab: 'mapa', source: 'live',
};

type UIState = Prefs & Ephemeral & {
  set: (p: Partial<Prefs & Ephemeral>) => void;
  setScope: (p: Partial<BoardScope>) => void;
  setMap: (p: Partial<MapPrefs>) => void;
};

export const useUI = create<UIState>()(
  persist(
    set => ({
      scope: DEFAULT_SCOPE, colorBy: 'urgency', sort: 'manual', insights: false, showOlderDone: false,
      agendaMode: 'week', hiddenCalendars: [], notesFilter: 'all', sidebarHidden: false, map: DEFAULT_MAP_PREFS,
      search: '', noteSearch: '', highlight: null, colorFilter: [], refDate: localToday(), selectedDay: localToday(), selectedNote: null, sidebarOpen: false,
      set: p => set(p),
      setScope: p => set(s => ({ scope: { ...s.scope, ...p } })),
      setMap: p => set(s => ({ map: { ...s.map, ...p } })),
    }),
    {
      name: 'quadro-ui-v3',
      partialize: s => ({
        scope: s.scope, colorBy: s.colorBy, sort: s.sort, insights: s.insights, showOlderDone: s.showOlderDone,
        agendaMode: s.agendaMode, hiddenCalendars: s.hiddenCalendars, notesFilter: s.notesFilter, sidebarHidden: s.sidebarHidden, map: s.map,
      }),
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<Prefs>;
        return { ...current, ...p, scope: { ...DEFAULT_SCOPE, ...p.scope }, map: { ...DEFAULT_MAP_PREFS, ...p.map } };
      },
    },
  ),
);
